import  { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import admin from "../assets/admin.png";
import logo from "../assets/newlogo.png";
import AddNewDropdown from "./AddNewResearch";
import MobileHeader from "./MobileHeader";
import { useProfile } from "../context/ProfileContext";
import { useTheme } from "../context/ThemeContext";
import useCurrentTeacher from '../hooks/useCurrentTeacher'
import useNotifications from "../hooks/useNotifications";
import Notifications from "./notifications/Notifications";
import useMessageList from "../hooks/useMessageList";
import axios from "axios";
import usePlaylist from "../hooks/usePlaylist";
import usePaidCourses from "../hooks/usePaidCourses";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const Header = ({
  hideSearchBar = false,
  hideHome = false,
  hideLogo = false,
  hideFilter = false,
  hideBell = false,
  onLogoClick,
}) => {
  const { isTeacher } = useCurrentTeacher();
  const { courses: playlistCourses } = usePlaylist();
  const { courses: paidCourses } = usePaidCourses();
  // console.log("The paid courses are:",paidCourses)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, setUnreadCount } = useNotifications();
  const { unreadMap } = useMessageList();
  const bellRef = useRef(null);
  const { profileId, setProfileId, setProfileData } = useProfile();
  const { clearTheme, setTheme } = useTheme();

  const toggleDropdown = (id) => {
    setOpenDropdown((prev) => (prev === id ? null : id));
    setIsProfileDropdownOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen((prev) => !prev);
    setIsDropdownOpen(false);
  };
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${BASE_URL}/user/profile/create-profile/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_X_API_KEY,
          },
        });

        const data = await res.json();
        if (res.ok) {
          setProfile(data.data);
          setProfileId(data.data?.id);
        } else {
          console.error("Profile fetch failed");
        }
      } catch (error) {
        console.error("Error fetching profile:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("theme");
    clearTheme();
    navigate("/");
  };

  const handleConfirmDelete = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      console.error("You are not logged in.");
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/user/delete-account/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
      });
      if (response.ok) {
        console.error("Account deleted successfully!");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        navigate("/");
      } else {
        const data = await response.json();
        alert(data.message || "Failed to delete account");
      }
    } catch (error) {
      alert("Error deleting account.");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query || !query.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    setSearchResults([]);
    try {
      const res = await fetch(
        `${BASE_URL}/user/publication/search-data/?title=${encodeURIComponent(
          query
        )}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_X_API_KEY
          },
        }
      );
      const data = await res.json();
      console.log(data);
      if (res.ok) {
        const exactMatchResults = (data.results || []).filter((user) =>
          user.name?.toLowerCase().includes(query.toLowerCase())
        );
        setSearchResults(exactMatchResults);
      } else {
        console.error("Search failed");
      }
    } catch (err) {
      console.error("Search error:", err.message);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("search") || "";
    setSearchTerm(query);
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleContentFilter = (type) => {
    const params = new URLSearchParams(location.search);
    if (type !== "all") {
      params.set("type", type);
    } else {
      params.delete("type");
    }

    const searchString = params.toString();
    navigate(`/home${searchString ? `?${searchString}` : ""}`, {
      replace: false,
    });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalUnreadMessages = Object.values(unreadMap).reduce(
    (acc, count) => acc + count,
    0
  );

  const handleReadAll = () => {
    setUnreadCount(0);
  };

  const markAllNotificationsRead = async (notificationsArray) => {
    if (!notificationsArray || notificationsArray.length === 0) return;

    const unreadIds = notificationsArray
      .filter((n) => !n.is_read)
      .map((n) => n.id);

    if (unreadIds.length === 0) return;

    try {
      await Promise.all(
        unreadIds.map((id) =>
          axios.patch(
            `${BASE_URL}/user/chat/notification/`,
            { notification_id: id },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                "Content-Type": "application/json",
                "x-api-key": process.env.REACT_APP_X_API_KEY
              },
            }
          )
        )
      );
      console.log("All notifications marked as read");
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  return (
//     <>
//       <div>
//         <MobileHeader />
//       </div>
//       <nav className="navbar-container desktop_header">
//         <div className="navbar">
//           <div className="nav-left">
//             <div className="logo_sec">
//               <div className="logo-link">
//                 <img
//                   src={logo}
//                   className="site-logo"
//                   alt="Logo"
//                   onClick={(e) => {
//                     onLogoClick?.(e);
//                     const token = localStorage.getItem("access_token");
//                     navigate(token ? "/home" : "/");
//                   }}
//                 />
//               </div>

             
//             </div>
//           </div>


//           {!hideSearchBar && profile && (
//             <div className="search-bar" ref={searchRef}>
//               <div className="header_search">
//                 <input
//                   type="search"
//                   className="search-bar-input"
//                   placeholder="Search for research, journals, people, etc."
//                   value={searchTerm}
//                   onChange={(e) => {
//                     const value = e.target.value;
//                     setSearchTerm(value);
//                     handleSearch(value);

//                     if (location.pathname === "/home") {
//                       const params = new URLSearchParams(location.search);
//                       if (value.trim()) {
//                         params.set("search", value.trim());
//                       } else {
//                         params.delete("search");
//                       }
//                       navigate(`/home?${params.toString()}`);
//                     }
//                   }}
//                   onKeyDown={(e) => {
//                     if (e.key === "Enter" && location.pathname !== "/home") {
//                       navigate(
//                         `/home?search=${encodeURIComponent(searchTerm)}`
//                       );
//                     }
//                   }}
//                 />
//                 <img
//                   src={searchIcon}
//                   className="headersearch-icon"
//                   alt="Search"
//                   onClick={() => {
//                     handleSearch(searchTerm);
//                     if (location.pathname !== "/home") {
//                       const params = new URLSearchParams(location.search);
//                       if (searchTerm.trim()) {
//                         params.set("search", searchTerm.trim());
//                       } else {
//                         params.delete("search");
//                       }

//                       navigate(`/home?${params.toString()}`);
//                     }
//                   }}
//                   style={{ cursor: "pointer" }}
//                 />
//               </div>

//               {isSearching && (
//                 <div className="search-loading">Searching...</div>
//               )}

//               {searchTerm.trim() && searchResults.length > 0 && (
//                 <div className="search-dropdown">
//                   <ul>
//                     {searchResults.map((user) => (
//                       <li key={user.id}>
//                         <div
//                           className="search-result-item"
//                           onClick={() => {
//                             setSearchResults([]);
//                             setProfileId(user.id);
//                             setProfileData(user);
//                             console.log("user: ", user.id);

//                             if (String(user.id) === String(profileId)) {
//                               navigate("/profile-view", {
//                                 state: {
//                                   isOwnProfile: true,
//                                   profileData: user,
//                                 },
//                               });
//                             } else {
//                               navigate(`/user-profile/${user.id}`, {
//                                 state: {
//                                   isOwnProfile: false,
//                                   profileData: user,
//                                 },
//                               });
//                             }
//                           }}
//                         >
//                           <img
//                             src={user.profile_picture || admin}
//                             alt={user.name}
//                             className="search-profile-img"
//                           />
//                           {user.name}
//                         </div>
//                       </li>
//                     ))}
//                   </ul>
//                 </div>
//               )}
//             </div>
//           )}

//           <div className="admin-icon user_login" ref={profileRef}>
//             {!hideBell && profile && (
//               <>
//                 <div ref={bellRef} style={{ position: "relative" }}>
//                   <button
//                     onClick={async () => {
//                       setOpen(!open);
//                       if (!open) {
//                         await markAllNotificationsRead(notifications);
//                         setUnreadCount(0);
//                       }
//                     }}
//                     className="bell-button"
//                   >
//                     <Bell size={24} stroke="#007bff" />
//                     {unreadCount > 0 && (
//                       <span className="notification-badge">{unreadCount}</span>
//                     )}{" "}
//                   </button>

//                   {open && <Notifications onReadAll={handleReadAll} />}
//                 </div>

//                 <div>
//                   <button
//                     onClick={() => navigate("/chat")}
//                     className="bell-button"
//                   >
//                     {" "}
//                     <MessageCircleMore size={24} stroke="#007bff" />{" "}
//                     {totalUnreadMessages > 0 && (
//                       <span className="notification-badge">
//                         {totalUnreadMessages}
//                       </span>
//                     )}
//                   </button>
//                 </div>
//               </>
//             )}
//             {!hideHome && (
//               <div className="home_link ">
//                 {location.pathname !== "/home" && (
//                   <Link to="/home" className="home-link">
                   
//                     <button className="bell-button">
//                       <House size={24} stroke="#007bff" />
//                     </button>
//                   </Link>
//                 )}
//               </div>
//             )}

//             <ul className="menu_list">
//               {!hideFilter && (
//                 <div className="content-filter">
//                   <select
//                     value={
//                       new URLSearchParams(location.search).get("type") || "all"
//                     }
//                     onChange={(e) => handleContentFilter(e.target.value)}
//                     className="filter-dropdown"
//                   >
//                     <option value="all">All</option>
//                     <option value="publication">Publication</option>
//                     <option value="video">Videos</option>
//                     <option value="image">Images</option>
//                     <option value="code">Code</option>
//                     <option value="post">Posts</option>
//                   </select>

                 
//                 </div>
//               )}
           
//               {!hideLogo && profile && (
//                 <li className="user_icon" onClick={toggleProfileDropdown}>
//                   <span>
//                     <img
//                       src={profile?.profile_picture || admin}
//                       alt="User"
//                       style={{
//                         width: "38px",
//                         height: "38px",
//                         borderRadius: "50%",
//                         objectFit: "cover",
//                       }}
//                     />
//                   </span>
//                 </li>
//               )}

//               <AddNewDropdown
//                 isOpen={openDropdown === "btn1"}
//                 onClose={() => setOpenDropdown(null)}
//               />
//             </ul>

//             {isProfileDropdownOpen && profile && (
//               <div className="profile-dropdown">
//                 <ul className="dropdownlist">
//                   <li className="dropdown-items">
//                     <Link
//                       to={{
//                         pathname: "/profile-view",
//                       }}
//                       state={{
//                         isOwnProfile: true,
//                         profileData: profile,
//                       }}
//                     >
//                       <p className="dropdown-items2">Your profile</p>
//                     </Link>{" "}
//                   </li>
//                   <li className="dropdown-items">
//                     <Link to="/changepassword">
//                       <p className="dropdown-items2">Change password</p>
//                     </Link>
//                   </li>
//                     <li className="dropdown-items">
        
//           {
//             isTeacher ? <Link to="/teacher">
//           <p className="dropdown-items2">Teacher Profile</p>
//         </Link> :  <Link to="/teacher-profile"  state={{
//             isOwnProfile: true,
//             profileData: profile,
//           }}> 
//           <p className="dropdown-items2">Become a Teacher</p>
//         </Link>
//           }
        
//       </li>
//       <li className="dropdown-items">
//         <Link to="/courses">
//           <p className="dropdown-items2">Browse Courses</p>
//         </Link>
//       </li>
//          <li className="dropdown-items">
//         {
//   (playlistCourses.length > 0 || paidCourses.length > 0) && (
//     <Link to="/student">
//       <p className="dropdown-items2">Enrolled Courses</p>
//     </Link>
//   )
// }


//       </li>
//                   <li
//                     className="dropdown-items"
//                     onClick={() => setShowDeleteModal(true)}
//                   >
//                     <p className="dropdown-items2">Delete Account</p>
//                   </li>
//                   <li className="dropdown-items logout" onClick={handleLogout}>
//                     <p className="dropdown-items2">Log out</p>
//                   </li>
//                 </ul>
//               </div>
//             )}
//           </div>
//         </div>
//       </nav >

//       {showDeleteModal && (
//         <div className="delete-modal-overlay">
//           <div className="delete-modal">
//             <h3>Are you sure you want to delete your account?</h3>
//             <p>This action cannot be undone.</p>
//             <div className="modal-actions">
//               <button className="confirm-btn" onClick={handleConfirmDelete}>
//                 Confirm
//               </button>
//               <button
//                 className="cancel-btn"
//                 onClick={() => setShowDeleteModal(false)}
//               >
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       )
//       }
//     </>
<>
  
    <MobileHeader />
  
  
  <nav className="hidden md:block w-full  flex items-center justify-center transition-all duration-300 sticky top-0 z-[999] px-6 py-4 bg-white border-b border-gray-100 shadow-sm ">
    <div className="w-full max-w-[1920px] mx-auto flex justify-between items-center gap-6">
      {/* Logo Section */}
      <div className="flex items-center gap-4 w-full max-w-[35%]">
        <div className="cursor-pointer">
          <img
            src={logo}
            className="w-full h-16  object-contain"
            alt="Logo"
            onClick={(e) => {
              onLogoClick?.(e);
              const token = localStorage.getItem("access_token");
              navigate(token ? "/home" : "/");
            }}
          />
        </div>
      </div>

      {/* Search Bar */}
      {!hideSearchBar && profile && (
        <div className="w-full max-w-[30%] relative" ref={searchRef}>
          <div className="relative">
            <input
              type="search"
              className="w-full px-5 py-3 pr-12 bg-white outline-none text-sm rounded-xl border border-gray-300 text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
              placeholder="Search for research, journals, people, etc."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                handleSearch(value);

                if (location.pathname === "/home") {
                  const params = new URLSearchParams(location.search);
                  if (value.trim()) {
                    params.set("search", value.trim());
                  } else {
                    params.delete("search");
                  }
                  navigate(`/home?${params.toString()}`);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && location.pathname !== "/home") {
                  navigate(
                    `/home?search=${encodeURIComponent(searchTerm)}`
                  );
                }
              }}
            />
            <div 
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer"
              onClick={() => {
                handleSearch(searchTerm);
                if (location.pathname !== "/home") {
                  const params = new URLSearchParams(location.search);
                  if (searchTerm.trim()) {
                    params.set("search", searchTerm.trim());
                  } else {
                    params.delete("search");
                  }
                  navigate(`/home?${params.toString()}`);
                }
              }}
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {isSearching && (
            <div className="mt-2 text-sm text-gray-500 flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              Searching...
            </div>
          )}

          {searchTerm.trim() && searchResults.length > 0 && (
            <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl shadow-lg border border-gray-200 max-h-80 overflow-y-auto z-20">
              <div className="p-2">
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSearchResults([]);
                      setProfileId(user.id);
                      setProfileData(user);
                      console.log("user: ", user.id);

                      if (String(user.id) === String(profileId)) {
                        navigate("/profile-view", {
                          state: {
                            isOwnProfile: true,
                            profileData: user,
                          },
                        });
                      } else {
                        navigate(`/user-profile/${user.id}`, {
                          state: {
                            isOwnProfile: false,
                            profileData: user,
                          },
                        });
                      }
                    }}
                  >
                    <div className="relative">
                      <img
                        src={user.profile_picture || admin}
                        alt={user.name}
                        className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                      />
                      <div
  className={`absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full border border-white ${
    user?.is_online ? 'bg-green-500' : 'bg-gray-400'
  }`}
/>

                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{user.name}</p>
                      <p className="text-xs text-gray-500">View profile</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Right Side Actions */}
      <div className="flex justify-end items-center gap-4 max-w-[35%] !important" ref={profileRef}>
        {!hideBell && profile && (
          <>
            <div style={{ position: "relative" }} ref={bellRef}>
              <button
                onClick={async () => {
                  setOpen(!open);
                  if (!open) {
                    await markAllNotificationsRead(notifications);
                    setUnreadCount(0);
                  }
                }}
                className="relative w-10 h-10 bg-[#e8f0f7] flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-semibold w-5 h-5 flex items-center justify-center rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>

              {open && <Notifications onReadAll={handleReadAll} />}
            </div>

            <div>
              <button
                onClick={() => navigate("/chat")}
                className="relative w-10 h-10 bg-[#e8f0f7] flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {totalUnreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-semibold w-5 h-5 flex items-center justify-center rounded-full">
                    {totalUnreadMessages}
                  </span>
                )}
              </button>
            </div>
          </>
        )}
        {!hideHome && (
          <div className="pr-4">
            {location.pathname !== "/home" && (
              <Link to="/home" className="hover:bg-transparent hover:shadow-none">
                <button className="relative w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </button>
              </Link>
            )}
          </div>
        )}

        <ul className="flex gap-4 list-none items-center w-max">
          {!hideFilter && (
            <div className="flex gap-4 items-center">
              <select
                value={
                  new URLSearchParams(location.search).get("type") || "all"
                }
                onChange={(e) => handleContentFilter(e.target.value)}
                className="px-4 py-2.5 text-sm font-medium border border-gray-100 rounded-xl bg-white text-gray-900 cursor-pointer outline-none appearance-none pr-10 hover:border-gray-400 focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2390A3BF' stroke-width='1.5'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center'
                }}
              >
                <option value="all">All</option>
                <option value="publication">Publications</option>
                <option value="video">Videos</option>
                <option value="image">Images</option>
                <option value="code">Code</option>
                <option value="post">Posts</option>
              </select>
            </div>
          )}

          {!hideLogo && profile && (
            <li className="cursor-pointer" onClick={toggleProfileDropdown}>
              <div className="w-12 h-12 flex justify-center items-center rounded-full p-1 overflow-hidden border-2 border-gray-50 hover:border-[#0f6abf] transition-colors">
                <img
                  src={profile?.profile_picture || admin}
                  alt="User"
                  className="w-full h-full object-cover"
                />
              </div>
            </li>
          )}

          <AddNewDropdown
            isOpen={openDropdown === "btn1"}
            onClose={() => setOpenDropdown(null)}
          />
        </ul>

        {isProfileDropdownOpen && profile && (
          <div className="absolute top-16 right-6 bg-white rounded-xl shadow-lg border border-gray-200 w-64 py-2 backdrop-blur-lg z-[1000]">
            <div className="p-2">
              <Link
                to={{
                  pathname: "/profile-view",
                }}
                state={{
                  isOwnProfile: true,
                  profileData: profile,
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-sm font-medium">Your Profile</span>
              </Link>
              
              <Link 
                to="/changepassword" 
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <span className="text-sm font-medium">Change Password</span>
              </Link>
              
              {isTeacher ? (
                <Link 
                  to="/teacher" 
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-sm font-medium">Teacher Profile</span>
                </Link>
              ) : (
                <Link
                  to="/teacher-profile"
                  state={{
                    isOwnProfile: true,
                    profileData: profile,
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-sm font-medium">Become a Teacher</span>
                </Link>
              )}
              
              <Link 
                to="/courses" 
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-sm font-medium">Browse Courses</span>
              </Link>
              <Link 
  to="/marketplace" 
  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
>
  <svg 
    className="w-4 h-4 text-gray-500" 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth="2" 
      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.5 7h13L17 13M7 13H5.4M17 13l1.5 7M6 21a1 1 0 100-2 1 1 0 000 2zm12 0a1 1 0 100-2 1 1 0 000 2z" 
    />
  </svg>
  <span className="text-sm font-medium">Marketplace</span>
</Link>

              {(playlistCourses.length > 0 || paidCourses.length > 0) && (
                <Link 
                  to="/student" 
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span className="text-sm font-medium">Enrolled Courses</span>
                </Link>
              )}
              
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-red-50 transition-colors text-red-500 cursor-pointer"
                onClick={() => setShowDeleteModal(true)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span className="text-sm font-medium">Delete Account</span>
              </div>
              
              <div className="border-t border-gray-200 mt-2 pt-2">
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer"
                  onClick={handleLogout}
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="text-sm font-medium">Log out</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </nav>

  {/* Delete Account Modal - Keep your existing modal */}
  {showDeleteModal && (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white p-8 rounded-2xl w-full max-w-md text-center shadow-xl">
        <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Delete Your Account?</h3>
        <p className="text-gray-600 mb-6">This action cannot be undone. All your data will be permanently removed.</p>
        <div className="flex gap-3">
          <button
            className="flex-1 px-5 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            className="flex-1 px-5 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition-colors"
            onClick={handleConfirmDelete}
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>
  )}
</>

  );
};

export default Header;

